import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  useTheme,
  useMediaQuery,
  Chip,
  Divider,
  Avatar,
  Stack,
  Fade,
} from '@mui/material';
import {
  ArrowForward,
  Inventory2,
  Speed,
  Security,
  Analytics,
  CloudQueue,
  QrCodeScanner,
  TrendingUp,
  CheckCircle,
  Star,
  BusinessCenter,
  Timeline,
  Shield,
  Devices,
  BarChart,
  KeyboardArrowRight,
  PlayArrow,
  Group,
  SupportAgent,
} from '@mui/icons-material';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/bundle';
import './LandingPage.css';

const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentSlide, setCurrentSlide] = useState(0);

  const heroSlides = [
    {
      title: 'AsseTrack',
      subtitle: 'Smart Inventory Management',
      tagline: 'Complete Asset Tracking Solution for Modern Business',
      description: 'Streamline your operations with real-time tracking, QR codes, and powerful analytics',
      image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1920&h=1080&fit=crop',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      title: 'AsseTrack',
      subtitle: 'Real-Time Visibility',
      tagline: 'Know Where Your Assets Are, Always',
      description: 'Monitor every item across multiple locations with instant updates and alerts',
      image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1920&h=1080&fit=crop',
      gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    },
    {
      title: 'AsseTrack',
      subtitle: 'Enterprise-Grade Platform',
      tagline: 'Scale Your Business with Confidence',
      description: 'Multi-warehouse support, production management, and seamless integrations',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1920&h=1080&fit=crop',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
  ];

  const features = [
    {
      icon: <Inventory2 sx={{ fontSize: 48 }} />,
      title: 'Inventory Management',
      description: 'Complete inventory control with real-time tracking, automated alerts, and comprehensive asset lifecycle management',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      icon: <BusinessCenter sx={{ fontSize: 48 }} />,
      title: 'Warehouse Operations',
      description: 'Multi-warehouse support with location hierarchy, stock optimization, and efficient space utilization strategies',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    },
    {
      icon: <Timeline sx={{ fontSize: 48 }} />,
      title: 'Production Management',
      description: 'Recipe-based production system with batch tracking, quality control, and manufacturing workflow automation',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
    {
      icon: <BarChart sx={{ fontSize: 48 }} />,
      title: 'Product Catalogue',
      description: 'Digital product catalog with detailed specifications, pricing, and cross-referencing capabilities',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    },
    {
      icon: <QrCodeScanner sx={{ fontSize: 48 }} />,
      title: 'QR Code System',
      description: 'Generate, print, and scan QR codes for instant asset identification, tracking, and mobile access',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    },
    {
      icon: <Shield sx={{ fontSize: 48 }} />,
      title: 'Advanced Security',
      description: 'Enterprise-grade security with role-based access, audit trails, and encrypted data protection',
      gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    },
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Enter Items',
      description: 'Add inventory items manually or import via CSV/Excel, assign categories, and generate QR codes for tracking',
      icon: <Inventory2 sx={{ fontSize: 56 }} />,
      color: '#667eea',
    },
    {
      step: '02',
      title: 'Production',
      description: 'Create production recipes, manage batch processing, track raw materials, and monitor manufacturing workflows',
      icon: <Timeline sx={{ fontSize: 56 }} />,
      color: '#764ba2',
    },
    {
      step: '03',
      title: 'Track & Transfer',
      description: 'Monitor inventory movements, manage warehouse transfers, and analyze performance with detailed reports',
      icon: <TrendingUp sx={{ fontSize: 56 }} />,
      color: '#4facfe',
    },
  ];

  const testimonials = [
    {
      name: 'John Smith',
      role: 'Operations Manager',
      company: 'TechCorp Inc.',
      content: 'AsseTrack transformed how we manage our inventory. The QR code scanning feature is a game-changer!',
      rating: 5,
      avatar: 'J',
    },
    {
      name: 'Sarah Johnson',
      role: 'IT Director',
      company: 'Global Solutions',
      content: 'The analytics dashboard gives us incredible insights into our asset utilization. Highly recommended!',
      rating: 5,
      avatar: 'S',
    },
    {
      name: 'Michael Chen',
      role: 'Warehouse Manager',
      company: 'LogiTech',
      content: 'Best inventory management system we have used. Simple, powerful, and reliable.',
      rating: 5,
      avatar: 'M',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut',
      },
    },
  };

  return (
    <Box sx={{ overflow: 'hidden', bgcolor: '#fafafa' }}>
      
      {/* Hero Section with Carousel */}
      <Box sx={{ position: 'relative', height: isMobile ? '80vh' : '100vh', mt: { xs: 0, md: 0 } }}>
        <Swiper
          autoplay={{ delay: 5000 }}
          pagination={{ clickable: true }}
          loop={true}
          className="hero-carousel"
          style={{ height: '100%' }}
        >
          {heroSlides.map((slide, index) => (
            <SwiperSlide key={index}>
              <Box
                sx={{
                  position: 'relative',
                  height: isMobile ? '80vh' : '100vh',
                  backgroundImage: `url(${slide.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: slide.gradient,
                    opacity: 0.85,
                  },
                }}
              >
                <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, pt: 10 }}>
                  <Grid container spacing={4} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                      >
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Typography
                          variant={isMobile ? 'h2' : 'h1'}
                          sx={{
                            color: 'white',
                            fontWeight: 900,
                            mb: 2,
                            lineHeight: 1.1,
                            textShadow: '3px 3px 12px rgba(0,0,0,0.3)',
                            fontSize: { xs: '3rem', md: '4.5rem' },
                            letterSpacing: '-0.02em',
                          }}
                        >
                          {slide.title}
                        </Typography>
                        <Typography
                          variant={isMobile ? 'h4' : 'h3'}
                          sx={{
                            color: 'rgba(255, 255, 255, 0.95)',
                            mb: 2,
                            fontWeight: 700,
                            lineHeight: 1.3,
                            textShadow: '2px 2px 8px rgba(0,0,0,0.2)',
                          }}
                        >
                          {slide.subtitle}
                        </Typography>
                        <Typography
                          variant={isMobile ? 'h5' : 'h4'}
                          sx={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            mb: 3,
                            fontWeight: 500,
                            lineHeight: 1.4,
                            textShadow: '1px 1px 6px rgba(0,0,0,0.2)',
                            fontSize: { xs: '1.3rem', md: '1.6rem' },
                          }}
                        >
                          {slide.tagline}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.8)',
                            mb: 4,
                            fontSize: { xs: '1rem', md: '1.1rem' },
                            lineHeight: 1.6,
                            maxWidth: '600px',
                            mx: 'auto',
                          }}
                        >
                          {slide.description}
                        </Typography>
                      </Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 2 }}>
                          <Button
                            variant="contained"
                            size="large"
                            onClick={() => navigate('/login')}
                            sx={{
                              bgcolor: 'white',
                              color: '#1976d2',
                              px: 8,
                              py: 3,
                              fontWeight: 900,
                              borderRadius: '32px',
                              fontSize: '1.4rem',
                              border: '3px solid transparent',
                              background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #1976d2, #7c4dff) border-box',
                              position: 'relative',
                              overflow: 'hidden',
                              textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                              '&:before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(135deg, #1976d220, #7c4dff20)',
                                opacity: 0,
                                transition: 'opacity 0.3s',
                              },
                              '&:hover': {
                                transform: 'translateY(-4px) scale(1.02)',
                                boxShadow: '0 20px 40px rgba(25, 118, 210, 0.4)',
                                bgcolor: '#f8f9fa',
                                color: '#1565c0',
                                '&:before': {
                                  opacity: 1,
                                },
                                '& .MuiButton-endIcon': {
                                  transform: 'translateX(4px)',
                                },
                              },
                              '& .MuiButton-endIcon': {
                                transition: 'transform 0.3s',
                                color: '#1976d2',
                              },
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                            endIcon={<ArrowForward />}
                          >
                            Login to AsseTrack
                          </Button>
                        </Stack>
                      </motion.div>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                      >
                        <Box
                          sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.15)',
                            backdropFilter: 'blur(20px)',
                            borderRadius: '32px',
                            p: 4,
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            boxShadow: '0 24px 64px rgba(0, 0, 0, 0.2)',
                          }}
                        >
                          <Box
                            sx={{
                              bgcolor: 'rgba(255, 255, 255, 0.95)',
                              borderRadius: '24px',
                              p: 3,
                              mb: 2,
                            }}
                          >
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#333' }}>
                              Live Dashboard
                            </Typography>
                            
                            {/* Mini Chart Preview */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                                Inventory Overview
                              </Typography>
                              <Box sx={{ 
                                height: 120, 
                                background: 'linear-gradient(135deg, #667eea20 0%, #764ba220 100%)',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                overflow: 'hidden'
                              }}>
                                {/* Simple bar chart visualization */}
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                                  {[65, 45, 80, 35, 90, 55, 70].map((height, i) => (
                                    <Box
                                      key={i}
                                      sx={{
                                        width: 12,
                                        height: `${height * 0.8}px`,
                                        background: `linear-gradient(180deg, #667eea 0%, #764ba2 100%)`,
                                        borderRadius: '4px 4px 0 0',
                                        animation: `grow 1s ease-out ${i * 0.1}s both`
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            </Box>

                            {/* Quick Stats */}
                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                              <Box sx={{ flex: 1, textAlign: 'center' }}>
                                <Typography variant="caption" sx={{ color: '#666' }}>In Stock</Typography>
                                <Typography variant="h6" sx={{ color: '#4caf50', fontWeight: 700 }}>847</Typography>
                              </Box>
                              <Box sx={{ flex: 1, textAlign: 'center' }}>
                                <Typography variant="caption" sx={{ color: '#666' }}>Low Stock</Typography>
                                <Typography variant="h6" sx={{ color: '#ff9800', fontWeight: 700 }}>23</Typography>
                              </Box>
                              <Box sx={{ flex: 1, textAlign: 'center' }}>
                                <Typography variant="caption" sx={{ color: '#666' }}>Out of Stock</Typography>
                                <Typography variant="h6" sx={{ color: '#f44336', fontWeight: 700 }}>5</Typography>
                              </Box>
                            </Box>

                            {/* Recent Activity */}
                            <Box>
                              <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                                Recent Activity
                              </Typography>
                              <Box sx={{ fontSize: '0.75rem', color: '#999' }}>
                                • Laptop #1234 checked out to John D.<br/>
                                • 50 units of Office Supplies added<br/>
                                • Warehouse B inventory updated
                              </Box>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box
                              sx={{
                                flex: 1,
                                bgcolor: 'rgba(255, 255, 255, 0.95)',
                                borderRadius: '16px',
                                p: 2,
                                border: '1px solid rgba(102, 126, 234, 0.2)'
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Inventory2 sx={{ fontSize: 16, color: '#667eea' }} />
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                  Total Items
                                </Typography>
                              </Box>
                              <Typography variant="h4" sx={{ fontWeight: 700, color: '#667eea' }}>
                                875
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#4caf50' }}>
                                ↑ 12% this month
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                flex: 1,
                                bgcolor: 'rgba(255, 255, 255, 0.95)',
                                borderRadius: '16px',
                                p: 2,
                                border: '1px solid rgba(118, 75, 162, 0.2)'
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Speed sx={{ fontSize: 16, color: '#764ba2' }} />
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                  Efficiency
                                </Typography>
                              </Box>
                              <Typography variant="h4" sx={{ fontWeight: 700, color: '#764ba2' }}>
                                94%
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#4caf50' }}>
                                ↑ 3% improvement
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </motion.div>
                    </Grid>
                  </Grid>
                </Container>
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: { xs: 12, md: 20 }, bgcolor: 'white' }}>
        <Container maxWidth="xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 12 } }}>
              <Chip
                label="Features"
                sx={{
                  mb: 3,
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  color: '#667eea',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                }}
              />
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                sx={{
                  mb: 3,
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Powerful Features
              </Typography>
              <Typography
                variant="h6"
                sx={{ 
                  maxWidth: 700, 
                  mx: 'auto', 
                  color: '#666',
                  lineHeight: 1.8,
                }}
              >
                Everything you need to manage your inventory efficiently with enterprise-grade tools
              </Typography>
            </Box>
            
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <motion.div variants={itemVariants}>
                    <Card
                      sx={{
                        height: '100%',
                        borderRadius: '24px',
                        p: 3,
                        background: 'rgba(255, 255, 255, 0.98)',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06)',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          transform: 'translateY(-12px)',
                          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.12)',
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: feature.gradient,
                          transform: 'scaleX(0)',
                          transformOrigin: 'left',
                          transition: 'transform 0.4s ease',
                        },
                        '&:hover::before': {
                          transform: 'scaleX(1)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '20px',
                            background: feature.gradient,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 3,
                            color: 'white',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                          }}
                        >
                          {feature.icon}
                        </Box>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            mb: 2, 
                            fontWeight: 700,
                            color: '#333',
                            fontSize: '1.2rem',
                          }}
                        >
                          {feature.title}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#666',
                            lineHeight: 1.7,
                            fontSize: '0.95rem',
                          }}
                        >
                          {feature.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box sx={{ py: { xs: 12, md: 20 }, bgcolor: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
        <Container maxWidth="xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 12 } }}>
              <Chip
                label="Process"
                sx={{
                  mb: 3,
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  color: '#667eea',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                }}
              />
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                sx={{
                  mb: 3,
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                How It Works
              </Typography>
              <Typography
                variant="h6"
                sx={{ 
                  maxWidth: 700, 
                  mx: 'auto', 
                  color: '#666',
                  lineHeight: 1.8,
                }}
              >
                Get started in three simple steps and transform your inventory management
              </Typography>
            </Box>
            
            <Grid container spacing={4} justifyContent="center">
              {howItWorks.map((step, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <motion.div variants={itemVariants}>
                    <Box sx={{ textAlign: 'center', position: 'relative' }}>
                      <Box
                        sx={{
                          position: 'relative',
                          display: 'inline-block',
                          mb: 4,
                        }}
                      >
                        <Box
                          sx={{
                            width: 140,
                            height: 140,
                            borderRadius: '32px',
                            background: `linear-gradient(135deg, ${step.color} 0%, ${step.color}dd 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                            '&:hover': {
                              transform: 'translateY(-8px) scale(1.05)',
                              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
                            },
                          }}
                        >
                          {step.icon}
                        </Box>
                        <Typography
                          variant="h3"
                          sx={{
                            position: 'absolute',
                            top: -12,
                            right: -12,
                            bgcolor: 'white',
                            color: step.color,
                            width: 48,
                            height: 48,
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 800,
                            fontSize: '1.2rem',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                          }}
                        >
                          {step.step}
                        </Typography>
                      </Box>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          mb: 2, 
                          fontWeight: 700,
                          color: '#333',
                        }}
                      >
                        {step.title}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: '#666',
                          lineHeight: 1.8,
                          fontSize: '1rem',
                          px: 2,
                        }}
                      >
                        {step.description}
                      </Typography>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ py: { xs: 12, md: 16 }, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Container maxWidth="xl">
          <Grid container spacing={4} justifyContent="center">
            {[
              { number: '10K+', label: 'Active Users', icon: <Group sx={{ fontSize: 40 }} /> },
              { number: '1M+', label: 'Assets Tracked', icon: <Inventory2 sx={{ fontSize: 40 }} /> },
              { number: '99.9%', label: 'Uptime', icon: <Speed sx={{ fontSize: 40 }} /> },
              { number: '24/7', label: 'Support', icon: <SupportAgent sx={{ fontSize: 40 }} /> },
            ].map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Box sx={{ textAlign: 'center', color: 'white' }}>
                    <Box sx={{ mb: 2, opacity: 0.9 }}>{stat.icon}</Box>
                    <Typography 
                      variant="h2" 
                      sx={{ 
                        fontWeight: 800, 
                        mb: 1,
                        fontSize: { xs: '2.5rem', md: '3.5rem' },
                      }}
                    >
                      {stat.number}
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 500,
                        opacity: 0.9,
                        fontSize: { xs: '1rem', md: '1.1rem' },
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ py: { xs: 12, md: 20 }, bgcolor: 'white' }}>
        <Container maxWidth="xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 12 } }}>
              <Chip
                label="Testimonials"
                sx={{
                  mb: 3,
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  color: '#667eea',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                }}
              />
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                sx={{
                  mb: 3,
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                What Our Customers Say
              </Typography>
              <Typography
                variant="h6"
                sx={{ 
                  maxWidth: 700, 
                  mx: 'auto', 
                  color: '#666',
                  lineHeight: 1.8,
                }}
              >
                Trusted by businesses worldwide to manage their inventory efficiently
              </Typography>
            </Box>
            
            <Swiper
              pagination={{ clickable: true }}
              loop={true}
              spaceBetween={20}
              slidesPerView={1}
              breakpoints={{
                640: {
                  slidesPerView: 1,
                },
                768: {
                  slidesPerView: 2,
                },
                1024: {
                  slidesPerView: 3,
                },
              }}
              className="testimonials-carousel"
            >
              {testimonials.map((testimonial, index) => (
                <SwiperSlide key={index}>
                  <Card
                    sx={{
                      mx: 2,
                      height: '100%',
                      borderRadius: '24px',
                      p: 3,
                      background: 'rgba(255, 255, 255, 0.98)',
                      border: '1px solid rgba(0, 0, 0, 0.05)',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.12)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', mb: 3 }}>
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} sx={{ color: '#ffc107', fontSize: 24 }} />
                        ))}
                      </Box>
                      <Typography
                        variant="body1"
                        sx={{ 
                          mb: 4, 
                          fontStyle: 'italic', 
                          color: '#333',
                          lineHeight: 1.8,
                          fontSize: '1.05rem',
                        }}
                      >
                        "{testimonial.content}"
                      </Typography>
                      <Divider sx={{ mb: 3 }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            width: 56,
                            height: 56,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            fontWeight: 700,
                            fontSize: '1.2rem',
                          }}
                        >
                          {testimonial.avatar}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
                            {testimonial.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666', fontSize: '0.9rem' }}>
                            {testimonial.role}, {testimonial.company}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 16, md: 24 }, bgcolor: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', textAlign: 'center' }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: '32px',
                p: { xs: 6, md: 10 },
                boxShadow: '0 24px 64px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <Typography 
                variant={isMobile ? 'h3' : 'h2'} 
                sx={{ 
                  mb: 3, 
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Ready to Transform Your Inventory Management?
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 6, 
                  color: '#666',
                  lineHeight: 1.8,
                  fontSize: { xs: '1.1rem', md: '1.2rem' },
                }}
              >
                Join thousands of businesses already using AsseTrack to streamline their operations
              </Typography>
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={3} 
                justifyContent="center"
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    px: 10,
                    py: 3.5,
                    fontSize: '1.4rem',
                    fontWeight: 800,
                    borderRadius: '40px',
                    border: '4px solid rgba(255, 255, 255, 0.3)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
                      opacity: 0,
                      transition: 'opacity 0.3s',
                    },
                    '&:after': {
                      content: '""',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      width: 0,
                      height: 0,
                      background: 'rgba(255, 255, 255, 0.3)',
                      borderRadius: '50%',
                      transform: 'translate(-50%, -50%)',
                      transition: 'width 0.6s, height 0.6s',
                    },
                    '&:hover': {
                      transform: 'translateY(-6px) scale(1.05)',
                      boxShadow: '0 25px 50px rgba(102, 126, 234, 0.5)',
                      border: '4px solid rgba(255, 255, 255, 0.5)',
                      '&:before': {
                        opacity: 1,
                      },
                      '&:after': {
                        width: '300px',
                        height: '300px',
                      },
                      '& .MuiButton-endIcon': {
                        transform: 'translateX(6px) rotate(90deg)',
                      },
                    },
                    '& .MuiButton-endIcon': {
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    },
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  endIcon={<ArrowForward />}
                >
                  Login Now
                </Button>
              </Stack>
              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 4,
                  color: '#999',
                  fontSize: '0.9rem',
                }}
              >
                No credit card required • 14-day free trial • Cancel anytime
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
